import { AfterContentInit, Component, computed, ContentChildren, effect, EventEmitter, input, Input, Output, QueryList, signal, TemplateRef, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IFaq, IFaqOrdered, FaqTemplateContext } from 'src/models/faq.model';
import { LiberroFaqItemDirective } from './liberro-faq-item.directive';

@Component({
  selector: 'liberro-faq',
  standalone: false,
  templateUrl: './liberro-faq.component.html',
  styleUrl: './liberro-faq.component.less'
})
export class LiberroFaqComponent implements AfterContentInit {
  private sanitizer = inject(DomSanitizer);

  @Input() public showMoreLinkLabel = '';
  @Input() public showMoreLinkUrl = '';
  @Output() public showMoreFaqs = new EventEmitter<void>();
  @Output() public activeChange = new EventEmitter<{ id: number; active: boolean }>();
  @Input() public alwaysCenter = false;
  @Input() public selectedId: number | null = null;
  @Input() public textArrowPadding = true;
  @Input() public basicPadding = true;
  @ContentChildren(LiberroFaqItemDirective) public faqItems!: QueryList<LiberroFaqItemDirective>;

  private faqItemsSignal = signal<LiberroFaqItemDirective[]>([]);
  public activeStatesSignal = signal<Map<number, boolean>>(new Map());
  public tooltipVisible = signal<Map<number, boolean>>(new Map());

  public constructor() {
    // Update signal when ContentChildren changes
    effect(() => {
      if (this.faqItems) {
        this.faqItemsSignal.set(this.faqItems.toArray());
      }
    });
  }

  public faqList = input<(IFaqOrdered | IFaq)[]>([]);

  public faqListToggleable = computed<(IFaqOrdered | IFaq)[]>(() => {
    const activeStates = this.activeStatesSignal();
    const items = this.faqItemsSignal();
    // If faqItems are provided via projection, use them
    if (items && items.length > 0) {
      return items.map((item, index) => {
        const id = item.faqId ?? (index + 1);
        const active = activeStates.has(id) ? activeStates.get(id)! : (item.active ?? false);
        return {
          id,
          title: item.liberroFaqItem,
          active,
          contentTemplate: item.templateRef as TemplateRef<FaqTemplateContext>,
          highlight: item.highlight,
          preventBottomBorder: item.preventBottomBorder,
          tooltipContent: item.tooltipContent,
        };
      });
    }
    // Otherwise, use the faqList input
    return this.faqList().map(faq => ({
      ...faq,
      contentHtml: faq.contentHtml ? this.sanitizer.bypassSecurityTrustHtml(faq.contentHtml as string) : undefined,
      active: faq.active ?? false
    }));
  });

  public ngAfterContentInit(): void {
    if (this.faqItems) {
      this.faqItemsSignal.set(this.faqItems.toArray());
    }
  }

  public onShowMoreClicked(): void {
    this.showMoreFaqs.emit();
  }

  public onActiveChange(faqId: number, active: boolean): void {
    const currentStates = new Map(this.activeStatesSignal());
    currentStates.set(faqId, active);
    this.activeStatesSignal.set(currentStates);
    this.activeChange.emit({ id: faqId, active });
  }

  public getFaqTemplateContext(faq: IFaq | IFaqOrdered): FaqTemplateContext {
    return { $implicit: faq };
  }

  public onTooltipVisibleChange(index: number, visible: boolean): void {
    const currentMap = new Map(this.tooltipVisible());
    currentMap.set(index, visible);
    this.tooltipVisible.set(currentMap);
  }

  public onCloseTooltip(index: number, event: Event): void {
    event.stopPropagation();
    const currentMap = new Map(this.tooltipVisible());
    currentMap.set(index, false);
    this.tooltipVisible.set(currentMap);
  }

  public isTooltipVisible(index: number): boolean {
    return this.tooltipVisible().get(index) ?? false;
  }
}
